import { type FunctionComponent, type ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "@/core/shared/v2/lib/utils";

interface IntersectionFadeInProps {
  children: ReactNode;
  className?: string;
  rootMargin?: string;
  threshold?: number;
  delay?: number;
  duration?: number;
}

export const IntersectionFadeIn: FunctionComponent<IntersectionFadeInProps> = ({
  children,
  className,
  rootMargin = "0px",
  threshold = 0,
  delay = 0,
  duration = 700,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      {
        rootMargin,
        threshold,
      },
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [rootMargin, threshold]);

  return (
    <div
      ref={ref}
      className={cn(
        "transition-all ease-out",
        isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
        className,
      )}
      style={{
        transitionDuration: `${duration}ms`,
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};
